import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { auditLogService, AuditLogEntry } from '../../api/services';

const AuditLog: React.FC = () => {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [actionFilter, setActionFilter] = useState('');

  useEffect(() => {
    auditLogService.list(page, 50)
      .then((p) => { setEntries(p.content); setTotalPages(Math.max(1, p.totalPages)); })
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'));
  }, [page]);

  const visible = actionFilter
    ? entries.filter((e) => e.action.toLowerCase().includes(actionFilter.toLowerCase()))
    : entries;

  const badgeColor = (action: string) => {
    if (action.startsWith('LOGIN_FAILED')) return 'bg-red-100 text-red-800';
    if (action === 'LOGIN' || action === '2FA_ENABLED') return 'bg-green-100 text-green-800';
    if (action === 'CREATE') return 'bg-blue-100 text-blue-800';
    if (action === 'UPDATE' || action === 'PLAN_CHANGE') return 'bg-yellow-100 text-yellow-800';
    if (action === 'DELETE' || action.startsWith('GDPR_')) return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Journal d'audit</h2>
      <p className="text-sm text-gray-500">
        Trace complète des actions sensibles : authentification, CRUD sur Document/Client/Article/Paiement,
        changements de plan, RGPD, signature PDF, remboursements.
      </p>
      <input value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} placeholder="Filtrer par action (ex: LOGIN, CREATE…)"
             className="w-full rounded-lg border border-gray-200 px-3 py-2" />

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Quand</th>
              <th className="px-4 py-3 text-left">Action</th>
              <th className="px-4 py-3 text-left">Entité</th>
              <th className="px-4 py-3 text-left">Détails</th>
              <th className="px-4 py-3 text-left">IP</th>
              <th className="px-4 py-3 text-left">Correlation</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-sm">
            {visible.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Aucune entrée</td></tr>}
            {visible.map((e) => (
              <tr key={e.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{new Date(e.createdAt).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block text-xs font-semibold rounded-full px-2 py-0.5 ${badgeColor(e.action)}`}>
                    {e.action}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">{e.entityType ? `${e.entityType}${e.entityId ? `#${e.entityId}` : ''}` : '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-600 max-w-md truncate" title={e.details}>{e.details || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{e.ipAddress || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-400 font-mono">{e.correlationId?.slice(0, 8) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button disabled={page === 0} onClick={() => setPage(page - 1)}
                className="text-sm text-blue-600 disabled:opacity-40">← Précédent</button>
        <span className="text-xs text-gray-500">Page {page + 1} / {totalPages}</span>
        <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}
                className="text-sm text-blue-600 disabled:opacity-40">Suivant →</button>
      </div>
    </div>
  );
};

export default AuditLog;
